import LabelTableCell from 'Components/LabelTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import type { Device } from 'Services/ServiceProviderManagement/Features/Device/types'
import { IS_WALTTI_SERVICE_ENVIRONMENT } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import InfoIcon from '@mui/icons-material/Info'
import {
  Chip,
  type ChipProps,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Tooltip
} from '@mui/material'
import { t } from 'ttag'

const StyledTableCell = styled(LabelTableCell)`
  width: 40%;
`

interface DeviceDetailsProps {
  device: Device
}

function DeviceDetails({ device }: Readonly<DeviceDetailsProps>) {
  const chipPropsMapping: Record<string, Pick<ChipProps, 'color' | 'label'>> = {
    ACTIVE: { color: 'success', label: t`Active` },
    PASSIVE: { color: 'default', label: t`Passive` }
  }

  function getChipPropsByActivity(status: boolean) {
    return chipPropsMapping[status ? 'ACTIVE' : 'PASSIVE']
  }

  return (
    <TableContainer component={Paper}>
      <Table sx={{ td: { border: 'none' } }} aria-label={t`Details table`}>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell>{t`Device name`}</StyledTableCell>
            <TableCell>{showValueOrHyphen(device.name)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Status`}</StyledTableCell>
            <TableCell>
              {<Chip size="small" {...getChipPropsByActivity(device.active)} />}
              {device.activity_end_date &&
                device.active &&
                (() => {
                  const activityEndsText = t`Activity ends`
                  return (
                    <Tooltip
                      title={`${activityEndsText} ${datetimeFormatter(device.activity_end_date)}`}
                      placement="top"
                    >
                      <IconButton>
                        <InfoIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  )
                })()}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Created`}</StyledTableCell>
            <TableCell>{datetimeFormatter(device.created_timestamp)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Activity end date`}</StyledTableCell>
            <TableCell>{datetimeFormatter(device.activity_end_date)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Source`}</StyledTableCell>
            <TableCell>{showValueOrHyphen(device.source)}</TableCell>
          </StyledTableRow>
          {IS_WALTTI_SERVICE_ENVIRONMENT && (
            <>
              <StyledTableRow>
                <StyledTableCell>{t`Directory alias`}</StyledTableCell>
                <TableCell>{showValueOrHyphen(device.directory_alias)}</TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>{t`Machine uuid`}</StyledTableCell>
                <TableCell>{showValueOrHyphen(device.machine_uuid)}</TableCell>
              </StyledTableRow>
            </>
          )}
          <StyledTableRow>
            <StyledTableCell>{t`Last modified`}</StyledTableCell>
            <TableCell>{datetimeFormatter(new Date(device.modified_timestamp))}</TableCell>
          </StyledTableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default DeviceDetails
